import BlogArticleTemplate from '../../components/BlogArticleTemplate.tsx';

const articleData = {
  meta: {
    title: 'Accessibilità Web 2025: Cosa Cambia con l\'European Accessibility Act',
    description: 'Dichiarazione di accessibilità, documentazione tecnica e requisiti WCAG 2.1: tutto quello che devi sapere per adeguare il tuo sito alle nuove normative EAA.',
    keywords: 'accessibilità web, european accessibility act, WCAG 2.1, dichiarazione accessibilità, EAA 2025, accessibilità sito web, AgID accessibilità',
    ogTitle: 'Accessibilità Web 2025: Cosa Cambia con l\'European Accessibility Act',
    ogDescription: 'Guida completa all\'EAA: requisiti, documenti obbligatori e come adeguare il tuo sito web.',
    slug: 'accessibilita-web-european-accessibility-act',
    publishedDate: '2026-05-20',
    category: 'Guide Pratiche',
    readTime: '8 min',
    heroImage: '/sfondi/blog.png',
    heroImageAlt: 'Accessibilità Web EAA',
    excerpt: 'Dichiarazione di accessibilità, documentazione tecnica e requisiti WCAG 2.1: tutto quello che devi sapere per adeguare il tuo sito alle nuove normative.'
  },

  toc: [
    { id: 'cosa-cambia', label: 'Cosa Cambia' },
    { id: 'chi-deve-adeguarsi', label: 'Chi Deve Adeguarsi' },
    { id: 'documenti', label: 'Documenti Obbligatori' },
    { id: 'wcag', label: 'Requisiti WCAG 2.1' },
    { id: 'come-adeguarsi', label: 'Come Adeguarsi' },
    { id: 'sanzioni', label: 'Sanzioni e Scadenze' }
  ],

  sectionOneContent: (
    <>
      <p>
        Con l'entrata in vigore dell'<strong>European Accessibility Act (Direttiva UE 2019/882)</strong>, recepita in Italia con il D.Lgs. 82/2022, le regole del gioco per chi offre servizi digitali cambiano radicalmente.
      </p>

      <p>
        Non si tratta più solo di buone pratiche: l'accessibilità web diventa un <strong>obbligo di legge</strong> per chiunque offra prodotti o servizi digitali ai consumatori.
      </p>

      <h2 id="cosa-cambia">Cosa Cambia con l'EAA</h2>

      <p>
        L'European Accessibility Act estende gli obblighi di accessibilità, finora limitati alla pubblica amministrazione, anche al <strong>settore privato</strong>. Questo significa che siti web, e-commerce, app e servizi digitali devono essere accessibili a tutti, incluse le persone con disabilità.
      </p>

      <div className="info-box highlight">
        <h3>Le Due Novità Principali</h3>
        <ul>
          <li><strong>Dichiarazione di accessibilità:</strong> Da pubblicare sul sito, con requisiti soddisfatti, limitazioni note e contatto per segnalazioni</li>
          <li><strong>Documentazione tecnica:</strong> Da presentare ad AgID in caso di verifica, con checklist di conformità e risultati delle scansioni</li>
        </ul>
      </div>

      <h2 id="chi-deve-adeguarsi">Chi Deve Adeguarsi</h2>

      <p>
        La normativa si applica a tutti i soggetti che offrono servizi digitali ai consumatori:
      </p>

      <ul>
        <li><strong>E-commerce</strong> e negozi online</li>
        <li><strong>Siti web aziendali</strong> con servizi al pubblico</li>
        <li><strong>App mobile</strong> e web app</li>
        <li><strong>Servizi bancari</strong> e finanziari online</li>
        <li><strong>Piattaforme</strong> di prenotazione e biglietteria</li>
      </ul>

      <div className="info-box">
        <h3>Microimprese Esenti</h3>
        <p>Le microimprese (meno di 10 dipendenti e fatturato annuo inferiore a 2 milioni di euro) sono esentate dall'obbligo, ma è comunque consigliato adeguarsi per raggiungere un pubblico più ampio.</p>
      </div>
    </>
  ),

  parallaxBoxes: [
    {
      title: 'Dichiarazione di Accessibilità',
      content: (
        <>
          <p>Il primo documento obbligatorio da <strong>pubblicare direttamente sul sito</strong>:</p>
          <ul>
            <li><strong>Stato di conformità:</strong> Totale, parziale o non conforme</li>
            <li><strong>Requisiti soddisfatti:</strong> Elenco delle linee guida rispettate</li>
            <li><strong>Limitazioni note:</strong> Contenuti non ancora accessibili e motivazioni</li>
            <li><strong>Contatto:</strong> Riferimento per segnalazioni di problemi</li>
            <li><strong>Data:</strong> Di redazione e ultimo aggiornamento</li>
          </ul>
          <p>iubenda mette a disposizione un template gratuito per crearla facilmente.</p>
        </>
      )
    },
    {
      title: 'Documentazione Tecnica',
      content: (
        <>
          <p>Il secondo documento, da <strong>presentare ad AgID su richiesta</strong>:</p>
          <ul>
            <li><strong>Checklist di conformità:</strong> Compilate secondo lo standard EN 301 549</li>
            <li><strong>Risultati scansioni:</strong> Report degli strumenti di verifica automatica</li>
            <li><strong>Test manuali:</strong> Risultati della navigazione da tastiera e screen reader</li>
            <li><strong>Piano di miglioramento:</strong> Interventi previsti per risolvere le non conformità</li>
          </ul>
        </>
      )
    },
    {
      title: 'Standard WCAG 2.1',
      content: (
        <>
          <p>Lo standard di riferimento è il <strong>WCAG 2.1 livello AA</strong>, basato su 4 principi:</p>
          <ul>
            <li><strong>Percepibile:</strong> Contenuti fruibili attraverso diversi sensi (testi alternativi, sottotitoli, contrasto)</li>
            <li><strong>Utilizzabile:</strong> Navigazione da tastiera, tempo sufficiente, nessun contenuto che provochi crisi</li>
            <li><strong>Comprensibile:</strong> Testi leggibili, comportamento prevedibile, aiuto nella compilazione</li>
            <li><strong>Robusto:</strong> Compatibilità con tecnologie assistive attuali e future</li>
          </ul>
        </>
      )
    }
  ],

  threeBoxes: [
    {
      number: '1',
      title: 'Verifica Automatica',
      content: (
        <>
          <p>Strumenti per una prima analisi:</p>
          <ul>
            <li><strong>Lighthouse:</strong> Integrato in Chrome DevTools</li>
            <li><strong>axe DevTools:</strong> Estensione browser dettagliata</li>
            <li><strong>WAVE:</strong> Analisi visuale dei problemi</li>
          </ul>
        </>
      )
    },
    {
      number: '2',
      title: 'Test Manuale',
      content: (
        <>
          <p>Verifiche che gli strumenti automatici non coprono:</p>
          <ul>
            <li><strong>Navigazione da tastiera:</strong> Tab, Enter, Escape</li>
            <li><strong>Screen reader:</strong> VoiceOver, NVDA</li>
            <li><strong>Zoom al 200%:</strong> Layout ancora leggibile?</li>
          </ul>
        </>
      )
    },
    {
      number: '3',
      title: 'Correzione e Monitoraggio',
      content: (
        <>
          <p>Un processo continuo:</p>
          <ul>
            <li><strong>Prioritizza:</strong> Prima gli errori critici (navigazione, form)</li>
            <li><strong>Documenta:</strong> Tieni traccia delle correzioni</li>
            <li><strong>Monitora:</strong> Verifica periodicamente, soprattutto dopo aggiornamenti</li>
          </ul>
        </>
      )
    }
  ],

  twoColumnSections: [
    {
      left: (
        <>
          <h3 id="wcag">Requisiti Tecnici Essenziali</h3>
          <ul>
            <li><strong>HTML semantico:</strong> Heading gerarchici (h1-h6), landmark ARIA</li>
            <li><strong>Testi alternativi:</strong> Attributo alt su tutte le immagini informative</li>
            <li><strong>Contrasto colori:</strong> Rapporto minimo 4.5:1 per il testo</li>
            <li><strong>Focus visibile:</strong> Indicatore chiaro sugli elementi interattivi</li>
            <li><strong>Form accessibili:</strong> Label associate, messaggi di errore chiari</li>
            <li><strong>Responsive:</strong> Fruibile su qualsiasi dispositivo</li>
          </ul>
        </>
      ),
      right: (
        <>
          <h3 id="come-adeguarsi">Errori Più Comuni</h3>
          <ul>
            <li><strong>Immagini senza alt:</strong> Gli screen reader non possono descriverle</li>
            <li><strong>Contrasto insufficiente:</strong> Testo grigio chiaro su bianco</li>
            <li><strong>Solo mouse:</strong> Elementi non raggiungibili da tastiera</li>
            <li><strong>Video senza sottotitoli:</strong> Contenuti inaccessibili per non udenti</li>
            <li><strong>PDF non taggati:</strong> Documenti illeggibili dagli screen reader</li>
            <li><strong>Animazioni senza prefers-reduced-motion:</strong> Problematiche per utenti sensibili</li>
          </ul>
        </>
      )
    }
  ],

  infoBoxContent: (
    <>
      <h2 id="sanzioni">Sanzioni e Scadenze</h2>
      <p>Le tempistiche e le conseguenze del mancato adeguamento:</p>
      <div className="info-box">
        <ul>
          <li><strong>Scadenza:</strong> L'obbligo è già in vigore dal 28 giugno 2025 per i nuovi prodotti e servizi</li>
          <li><strong>Verifica:</strong> AgID può effettuare controlli e richiedere la documentazione tecnica</li>
          <li><strong>Segnalazioni:</strong> Qualsiasi utente può segnalare problemi di accessibilità ad AgID</li>
          <li><strong>Sanzioni:</strong> Previste sanzioni amministrative per il mancato adeguamento</li>
        </ul>
      </div>
    </>
  ),

  conclusionContent: (
    <>
      <p>
        L'<strong>accessibilità web</strong> non è solo un obbligo normativo: è un'opportunità per raggiungere un pubblico più ampio e offrire un'esperienza migliore a tutti gli utenti.
      </p>
      <p>
        Inizia con un audit del tuo sito, correggi i problemi critici e pubblica la dichiarazione di accessibilità. È un investimento che ripaga in termini di inclusione, SEO e reputazione.
      </p>
    </>
  ),

  ctaBox: {
    title: 'Vuoi Rendere il Tuo Sito Accessibile?',
    description: 'Ti aiuto ad adeguare il tuo sito web alle normative sull\'accessibilità. Audit, correzioni e dichiarazione inclusi.',
    buttonText: 'Richiedi un Audit'
  },

  relatedPosts: [
    {
      slug: 'ui-ux-design-perche-fondamentale',
      title: 'UI/UX Design: perché è fondamentale',
      category: 'UI/UX Design',
      readTime: '7 min'
    },
    {
      slug: 'restyling-sito-aziendale-quando-rinnovare',
      title: 'Restyling sito aziendale: quando rinnovare?',
      category: 'Web Design',
      readTime: '6 min'
    },
    {
      slug: 'quanto-costa-sito-web-professionale-2025',
      title: 'Quanto costa un sito web professionale?',
      category: 'Prezzi e Costi',
      readTime: '8 min'
    }
  ]
};

function AccessibilitaWebEAA() {
  return <BlogArticleTemplate {...articleData} />;
}

export default AccessibilitaWebEAA;
